import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Settings, Building2, MapPin, BarChart3, Plus, Trash2, Save, AlertTriangle, RefreshCw, ArrowRightLeft, ArrowUp, ArrowDown, ChevronDown, ChevronRight, Target } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, ReferenceLine } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const formatEur = (v) => {
  if (v == null || isNaN(v)) return "-";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};

const formatPct = (v) => {
  if (v == null || isNaN(v)) return "-";
  return `${v.toFixed(1)}%`;
};

const LOCATION_COLORS = ["#2563eb", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

const CE_ROWS = [
  { key: "revenue", label: "RICAVI", style: "header" },
  { key: "variable_costs", label: "(-) Costi Variabili", style: "cost" },
  { key: "margin_contribution", label: "MARGINE DI CONTRIBUZIONE", style: "subtotal_green" },
  { key: "fixed_costs", label: "(-) Costi Fissi", style: "cost" },
  { key: "ebitda", label: "EBITDA (MOL)", style: "subtotal_blue" },
  { key: "depreciation", label: "(-) Ammortamenti", style: "cost" },
  { key: "ebit", label: "EBIT", style: "subtotal_indigo" },
  { key: "financial_charges", label: "(-) Oneri Finanziari", style: "cost" },
  { key: "result_before_tax", label: "Risultato Ante Imposte", style: "subtotal" },
  { key: "taxes", label: "(-) Imposte", style: "cost" },
  { key: "net_result", label: "RISULTATO NETTO", style: "subtotal_indigo" },
  { key: "admin_compensation", label: "(-) Compenso Amministratore", style: "cost" },
  { key: "brand_royalties", label: "(-) Brand Royalties", style: "cost" },
  { key: "net_effective", label: "UTILE EFFETTIVO", style: "total" },
];

// Nuova struttura CE per Sede: due margini + costi indiretti ÷ N sedi
const CE_ROWS_LOC = [
  { key: "revenue", label: "RICAVI", style: "header" },
  { key: "variable_costs", label: "(-) Costi Variabili (% ricavi)", style: "cost", indent: 1 },
  { key: "first_margin", label: "1° MARGINE (di contribuzione)", style: "subtotal_green" },
  { key: "direct_personnel", label: "(-) Costi dipendenti", style: "cost", indent: 1 },
  { key: "direct_rent", label: "(-) Locazione", style: "cost", indent: 1 },
  { key: "direct_energy", label: "(-) Energia elettrica", style: "cost", indent: 1 },
  { key: "direct_other", label: "(-) Altri costi diretti sede", style: "cost", indent: 1 },
  { key: "direct_costs_total", label: "Tot. Costi diretti imputabili", style: "subtotal", indent: 0 },
  { key: "second_margin", label: "2° MARGINE (di sede)", style: "subtotal_green" },
  { key: "indirect_fixed_costs", label: "(-) Costi fissi indiretti (÷ N sedi)", style: "cost" },
  { key: "ebitda", label: "EBITDA (MOL)", style: "subtotal_blue" },
  { key: "depreciation", label: "(-) Ammortamenti (÷ N)", style: "cost" },
  { key: "ebit", label: "EBIT", style: "subtotal_indigo" },
  { key: "financial_charges", label: "(-) Oneri Finanziari (÷ N)", style: "cost" },
  { key: "result_before_tax", label: "Risultato Ante Imposte", style: "subtotal" },
  { key: "taxes", label: "(-) Imposte (÷ N)", style: "cost" },
  { key: "net_result", label: "RISULTATO NETTO", style: "subtotal_indigo" },
  { key: "admin_compensation", label: "(-) Compenso Amministratore (÷ N)", style: "cost" },
  { key: "brand_royalties", label: "(-) Brand Royalties (÷ N)", style: "cost" },
  { key: "net_effective", label: "UTILE EFFETTIVO", style: "total" },
];

// --- CE Annuale per Sede ---
const fmtV = (v) => {
  if (v == null || isNaN(v)) return "-";
  return new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(v);
};
const fmtP = (v) => (v == null || isNaN(v)) ? "-" : `${v.toFixed(1)}%`;

const ceRowStyle = (style) => {
  switch (style) {
    case 'header': return 'bg-slate-800 text-white font-bold';
    case 'subtotal_green': return 'bg-emerald-50 font-semibold text-emerald-800';
    case 'subtotal_blue': return 'bg-blue-50 font-semibold text-blue-800';
    case 'subtotal_indigo': return 'bg-indigo-50 font-semibold text-indigo-800';
    case 'subtotal': return 'bg-slate-100 font-semibold';
    case 'total': return 'bg-amber-50 font-bold text-amber-900 border-t-2 border-amber-300';
    case 'cost': return 'bg-white text-red-700';
    default: return 'bg-white';
  }
};

const VarArrow = ({ val, isCost }) => {
  if (val == null || isNaN(val) || val === 0) return <span className="text-slate-300">-</span>;
  const d = isCost ? -val : val;
  const color = d > 0 ? 'text-emerald-600' : 'text-red-500';
  const icon = d > 0 ? <ArrowUp className="h-2.5 w-2.5 inline" /> : <ArrowDown className="h-2.5 w-2.5 inline" />;
  return <span className={`${color} text-[10px] tabular-nums`}>{icon}{fmtV(Math.abs(val))}</span>;
};

const MONTHS_SHORT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

const LocationYearlyCESection = ({ yearlyCE, expandedLocCE, setExpandedLocCE }) => {
  const { year, locations, location_data } = yearlyCE;
  const [compareType, setCompareType] = React.useState('ap'); // 'ap' or 'prev_month'
  const [expandedDetails, setExpandedDetails] = React.useState({}); // {locName_ceKey: true}

  const toggleLoc = (loc) => {
    setExpandedLocCE(prev => ({ ...prev, [loc]: !prev[loc] }));
  };

  const toggleDetail = (loc, ceKey) => {
    const k = `${loc}_${ceKey}`;
    setExpandedDetails(prev => ({ ...prev, [k]: !prev[k] }));
  };

  // CE keys that can have account details
  const expandableKeys = new Set(['revenue', 'variable_costs', 'fixed_costs', 'depreciation', 'financial_charges', 'taxes']);

  // Macro-category icons and colors
  const macroStyle = {
    'Personale dipendente': { color: 'text-violet-600', bg: 'bg-violet-100', icon: '👤' },
    'Utenze': { color: 'text-orange-600', bg: 'bg-orange-100', icon: '⚡' },
    'Locazioni': { color: 'text-blue-600', bg: 'bg-blue-100', icon: '🏢' },
    'Altro': { color: 'text-slate-600', bg: 'bg-slate-100', icon: '📋' },
  };

  return (
    <div className="space-y-3" data-testid="location-yearly-ce-section">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" /> CE Annuale per Sede - {year}
            </CardTitle>
            <div className="flex items-center border rounded-md overflow-hidden" data-testid="ce-sede-compare-toggle">
              <button
                className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${compareType === 'ap' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
                onClick={() => setCompareType('ap')}
                data-testid="ce-sede-compare-ap"
              >vs AP</button>
              <button
                className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${compareType === 'prev_month' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
                onClick={() => setCompareType('prev_month')}
                data-testid="ce-sede-compare-prev-month"
              >vs Mese Prec.</button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {compareType === 'ap' ? `Confronto con stesso periodo ${year - 1}` : 'Confronto con mese precedente'} · {locations.length} sedi
          </p>
        </CardHeader>
      </Card>

      {locations.map((loc, idx) => {
        const locData = location_data[loc];
        if (!locData) return null;
        const isExpanded = expandedLocCE[loc] !== false; // default expanded
        const activeMonths = locData.months.filter(m => m.has_data);
        const color = LOCATION_COLORS[idx % LOCATION_COLORS.length];

        return (
          <Card key={loc} data-testid={`ce-sede-card-${idx}`}>
            <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleLoc(loc)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5" style={{ color }} />
                  {loc}
                  <Badge variant="outline" className="text-[10px]">{activeMonths.length}/12 mesi</Badge>
                  {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                </CardTitle>
                <div className="flex gap-3 text-xs">
                  <span>Ricavi: <strong>{fmtV(locData.totals.revenue)}</strong></span>
                  <span>EBITDA: <strong className={locData.totals.ebitda >= 0 ? 'text-emerald-600' : 'text-red-500'}>{fmtV(locData.totals.ebitda)}</strong></span>
                  <span>Utile Effettivo: <strong className={(locData.totals.net_effective ?? locData.totals.net_result) >= 0 ? 'text-emerald-600' : 'text-red-500'}>{fmtV(locData.totals.net_effective ?? locData.totals.net_result)}</strong></span>
                </div>
              </div>
            </CardHeader>
            {isExpanded && (
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px] border-collapse" data-testid={`ce-sede-table-${idx}`}>
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left px-2 py-1.5 min-w-[280px] sticky left-0 bg-slate-50 z-10 border-r-2 border-slate-300">Voce</th>
                        {activeMonths.map(m => (
                          <th key={m.month} colSpan={3} className="text-center px-0.5 py-1.5 border-r-2 border-slate-200 min-w-[150px]">
                            {m.month_name}
                          </th>
                        ))}
                        <th colSpan={3} className="text-center px-0.5 py-1.5 bg-slate-200 font-bold min-w-[150px]">TOTALE</th>
                      </tr>
                      <tr className="bg-slate-100 border-b-2 border-slate-300">
                        <th className="sticky left-0 bg-slate-100 z-10 border-r-2 border-slate-300"></th>
                        {activeMonths.map(m => (
                          <React.Fragment key={m.month}>
                            <th className="text-right px-1 py-0.5 text-[9px] font-medium text-slate-600 min-w-[50px]">Val.</th>
                            <th className="text-right px-1 py-0.5 text-[9px] font-medium text-slate-500 min-w-[36px]">%</th>
                            <th className="text-right px-1 py-0.5 text-[9px] font-medium text-slate-500 min-w-[50px] border-r-2 border-slate-200">
                              {compareType === 'ap' ? 'ΔAP' : 'ΔMP'}
                            </th>
                          </React.Fragment>
                        ))}
                        <th className="text-right px-1 py-0.5 text-[9px] font-semibold bg-slate-200 min-w-[50px]">Val.</th>
                        <th className="text-right px-1 py-0.5 text-[9px] font-semibold bg-slate-200 min-w-[36px]">%</th>
                        <th className="text-right px-1 py-0.5 text-[9px] font-semibold bg-slate-200 min-w-[50px]">
                          {compareType === 'ap' ? 'ΔAP' : ''}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {CE_ROWS.map(row => {
                        const isCost = row.style === 'cost';
                        const rowCss = ceRowStyle(row.style);
                        return (
                          <tr key={row.key} className={`${rowCss} border-b border-slate-100 hover:bg-slate-50/50`}>
                            <td className={`px-2 py-1 whitespace-nowrap sticky left-0 z-10 border-r-2 border-slate-300 ${rowCss}`}>
                              {row.label}
                            </td>
                            {activeMonths.map(m => {
                              const val = m.values?.[row.key];
                              const displayVal = val != null ? (isCost && val > 0 ? -val : val) : null;
                              const pct = row.key === 'revenue' ? null : m.pct?.[row.key];
                              const diffVal = compareType === 'ap' ? m.diff_yoy?.[row.key] : m.diff_prev_month?.[row.key];
                              return (
                                <React.Fragment key={m.month}>
                                  <td className={`text-right px-1 py-1 tabular-nums ${row.style === 'header' ? '' : ''}`}>{displayVal != null ? fmtV(displayVal) : <span className="text-slate-300">-</span>}</td>
                                  <td className={`text-right px-1 py-1 tabular-nums ${row.style === 'header' ? '' : 'text-slate-500'}`}>{pct != null ? fmtP(pct) : <span className="text-slate-300">-</span>}</td>
                                  <td className="text-right px-1 py-1 tabular-nums border-r-2 border-slate-200">
                                    <VarArrow val={diffVal} isCost={isCost} />
                                  </td>
                                </React.Fragment>
                              );
                            })}
                            <td className="text-right px-1 py-1 tabular-nums font-semibold bg-slate-50 text-slate-900">
                              {(() => {
                                const tv = locData.totals[row.key];
                                return tv != null ? fmtV(isCost && tv > 0 ? -tv : tv) : '-';
                              })()}
                            </td>
                            <td className="text-right px-1 py-1 tabular-nums text-slate-700 bg-slate-50">
                              {row.key === 'revenue' ? '100%' : fmtP(locData.totals_pct[row.key])}
                            </td>
                            <td className="text-right px-1 py-1 tabular-nums bg-slate-50 text-slate-900">
                              {compareType === 'ap' && locData.has_previous_year ? (
                                <VarArrow val={locData.totals[row.key] - (locData.prev_totals[row.key] || 0)} isCost={isCost} />
                              ) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Cost Groups: Personale, Utenze, Locazioni */}
                      {locData.cost_groups && Object.keys(locData.cost_groups).length > 0 && (
                        <>
                          <tr className="bg-slate-800 text-white">
                            <td colSpan={activeMonths.length * 3 + 4} className="px-2 py-1.5 text-[10px] font-bold sticky left-0 z-10">
                              DETTAGLIO COSTI PER CATEGORIA
                            </td>
                          </tr>
                          {['Personale dipendente', 'Utenze', 'Locazioni', 'Altro'].map(macro => {
                            const grp = locData.cost_groups[macro];
                            if (!grp || grp.amount === 0) return null;
                            const ms = macroStyle[macro] || macroStyle['Altro'];
                            const isOpen = expandedDetails[`${loc}_${macro}`];
                            return (
                              <React.Fragment key={macro}>
                                <tr className={`border-b border-slate-200 cursor-pointer hover:bg-slate-50 ${ms.bg}`} onClick={() => toggleDetail(loc, macro)}>
                                  <td className={`px-2 py-1.5 sticky left-0 z-10 border-r-2 border-slate-300 ${ms.bg}`}>
                                    <span className="flex items-center justify-between gap-1">
                                      <span className="flex items-center gap-1.5">
                                        {isOpen ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
                                        <span className={`font-semibold ${ms.color}`}>{macro}</span>
                                        <Badge variant="outline" className="text-[8px] py-0">{grp.accounts.length}</Badge>
                                      </span>
                                      <span className="flex items-center gap-2 flex-shrink-0">
                                        <span className="tabular-nums font-bold text-slate-800">{fmtV(-Math.abs(grp.amount))}</span>
                                        <span className={`tabular-nums font-bold ${ms.color} min-w-[40px] text-right`}>{fmtP(grp.pct)}</span>
                                      </span>
                                    </span>
                                  </td>
                                  {activeMonths.map(m => (
                                    <React.Fragment key={m.month}>
                                      <td colSpan={3} className={`border-r-2 border-slate-200 ${ms.bg}`}></td>
                                    </React.Fragment>
                                  ))}
                                  <td colSpan={3} className={ms.bg}></td>
                                </tr>
                                {isOpen && grp.accounts.map((acct, ai) => (
                                  <tr key={`${macro}_${ai}`} className="bg-white border-b border-slate-100 text-[9px]">
                                    <td className="px-2 py-0.5 sticky left-0 z-10 border-r-2 border-slate-300 bg-white" style={{ paddingLeft: '32px' }}>
                                      <span className="flex items-center justify-between gap-1">
                                        <span className="flex items-center gap-1 min-w-0">
                                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${acct.source === 'direct' ? 'bg-blue-500' : 'bg-amber-400'}`}></span>
                                          <span className="truncate text-slate-600">{acct.name}</span>
                                        </span>
                                        <span className="tabular-nums text-slate-700 flex-shrink-0">{fmtV(-Math.abs(acct.amount))}</span>
                                      </span>
                                    </td>
                                    {activeMonths.map(m => (
                                      <React.Fragment key={m.month}>
                                        <td colSpan={3} className="border-r-2 border-slate-200"></td>
                                      </React.Fragment>
                                    ))}
                                    <td colSpan={3}></td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export const LocationAnalysisPage = ({ clientId, year }) => {

  const [config, setConfig] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [detected, setDetected] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [trend, setTrend] = useState(null);
  const [breakEven, setBreakEven] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [yearlyCE, setYearlyCE] = useState(null);
  const [expandedLocCE, setExpandedLocCE] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [setupLocations, setSetupLocations] = useState([]);
  const [setupMethod, setSetupMethod] = useState("revenue_pct");
  const [assignments, setAssignments] = useState({});
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/multi-location`, { headers });
      setConfig(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [clientId]);

  const fetchAnalysis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/location-analysis?year=${year}`, { headers });
      setAnalysis(res.data);
    } catch {
      setAnalysis(null);
    }
  }, [clientId, year]);

  const fetchMonthly = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/location-monthly?year=${year}`, { headers });
      setMonthly(res.data);
    } catch {
      setMonthly(null);
    }
  }, [clientId, year]);

  const fetchTrend = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/location-trend`, { headers });
      setTrend(res.data);
    } catch {
      setTrend(null);
    }
  }, [clientId]);

  const fetchBreakEven = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/location-break-even?year=${year}`, { headers });
      setBreakEven(res.data);
    } catch {
      setBreakEven(null);
    }
  }, [clientId, year]);

  const fetchHistoryTarget = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/location-history-and-target?year=${year}&target_month=9`, { headers });
      setHistoryTarget(res.data);
    } catch {
      setHistoryTarget(null);
    }
  }, [clientId, year]);

  const fetchYearlyCE = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/location-yearly-ce?year=${year}`, { headers });
      setYearlyCE(res.data);
    } catch {
      setYearlyCE(null);
    }
  }, [clientId, year]);

  const fetchDetected = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/detect-locations?year=${year}`, { headers });
      setDetected(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [clientId, year]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const cfg = await fetchConfig();
      if (cfg?.enabled) {
        await Promise.all([fetchAnalysis(), fetchMonthly(), fetchTrend(), fetchYearlyCE(), fetchBreakEven(), fetchHistoryTarget()]);
      }
      setLoading(false);
    };
    init();
  }, [fetchConfig, fetchAnalysis, fetchMonthly, fetchTrend, fetchYearlyCE, fetchBreakEven, fetchHistoryTarget]);

  const openSetup = async () => {
    const det = await fetchDetected();
    const cfg = await fetchConfig();
    if (cfg?.locations?.length) {
      setSetupLocations(cfg.locations);
    } else if (det?.locations?.length) {
      setSetupLocations(det.locations);
    }
    setSetupMethod(cfg?.allocation_method || "revenue_pct");
    setAssignments(cfg?.assignments || {});
    setShowSetup(true);
  };

  const saveSetup = async () => {
    if (setupLocations.length < 2) {
      toast.error("Servono almeno 2 sedi");
      return;
    }
    setSaving(true);
    try {
      // Save config
      await axios.put(`${API}/clients/${clientId}/multi-location`, {
        locations: setupLocations.filter(l => l.trim()),
        allocation_method: setupMethod,
      }, { headers });

      // Save assignments
      const assignList = Object.entries(assignments).map(([code, assignment]) => ({
        account_code: code, assignment
      }));
      if (assignList.length > 0) {
        await axios.post(`${API}/clients/${clientId}/multi-location/assignments`, {
          assignments: assignList,
        }, { headers });
      }

      toast.success("Configurazione multi-sede salvata");
      setShowSetup(false);
      setLoading(true);
      await fetchConfig();
      await Promise.all([fetchAnalysis(), fetchMonthly(), fetchTrend(), fetchYearlyCE(), fetchBreakEven()]);
      setLoading(false);
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const disableMultiLocation = async () => {
    try {
      await axios.delete(`${API}/clients/${clientId}/multi-location`, { headers });
      toast.success("Multi-sede disabilitato");
      setConfig(prev => ({ ...prev, enabled: false }));
      setAnalysis(null);
    } catch {
      toast.error("Errore");
    }
  };

  if (loading) return <div className="flex justify-center py-12 text-muted-foreground">Caricamento analisi per sede...</div>;

  // Not enabled - show activation panel
  if (!config?.enabled) {
    return (
      <div className="space-y-6" data-testid="location-analysis-page">
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-slate-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Analisi Multi-Sede</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Attiva questa sezione per generare un Conto Economico distinto per ogni sede/unita locale.
              Il sistema rileva automaticamente le sedi dai nomi dei conti caricati.
            </p>
            <Button onClick={openSetup} data-testid="enable-multi-location-btn">
              <MapPin className="h-4 w-4 mr-2" /> Configura Sedi
            </Button>
          </CardContent>
        </Card>
        <SetupDialog
          open={showSetup}
          onClose={() => setShowSetup(false)}
          setupLocations={setupLocations}
          setSetupLocations={setSetupLocations}
          setupMethod={setupMethod}
          setSetupMethod={setSetupMethod}
          assignments={assignments}
          setAssignments={setAssignments}
          detected={detected}
          saving={saving}
          onSave={saveSetup}
        />
      </div>
    );
  }

  // Enabled but no analysis data
  if (!analysis || analysis.error) {
    return (
      <div className="space-y-6" data-testid="location-analysis-page">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" /> CE per Sede - {year}
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openSetup}><Settings className="h-4 w-4 mr-1" /> Configura</Button>
            <Button variant="ghost" size="sm" onClick={disableMultiLocation} className="text-red-500">Disattiva</Button>
          </div>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
            <p className="text-muted-foreground">{analysis?.error || "Nessun dato disponibile. Carica i bilanci e configura le sedi."}</p>
          </CardContent>
        </Card>
        <SetupDialog
          open={showSetup} onClose={() => setShowSetup(false)}
          setupLocations={setupLocations} setSetupLocations={setSetupLocations}
          setupMethod={setupMethod} setSetupMethod={setSetupMethod}
          assignments={assignments} setAssignments={setAssignments}
          detected={detected} saving={saving} onSave={saveSetup}
        />
      </div>
    );
  }

  // (chart data per la sezione superiore rimossi nella riprogettazione UI)

  return (
    <div className="space-y-6" data-testid="location-analysis-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" /> CE per Sede - {year}
          </h2>
          <p className="text-sm text-muted-foreground">
            {analysis.months_loaded} mesi caricati · Ricavi: {analysis.has_manual_revenue ? <strong>ripartiti per sede in base ai corrispettivi (Excel)</strong> : 'dai bilanci'} · Costi: dai bilanci mensili · Costi fissi indiretti: <strong>÷ {analysis.n_locations || analysis.locations?.length} sedi</strong>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={async () => { setLoading(true); await Promise.all([fetchAnalysis(), fetchMonthly(), fetchTrend(), fetchYearlyCE(), fetchBreakEven()]); setLoading(false); }} data-testid="refresh-analysis-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Aggiorna
          </Button>
          <Button variant="outline" size="sm" onClick={openSetup} data-testid="configure-locations-btn">
            <Settings className="h-4 w-4 mr-1" /> Configura
          </Button>
        </div>
      </div>

      {/* Summary KPI Cards — una per sede + Azienda */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {analysis.location_results.map((lr, i) => {
          const ebitda = lr.total.ebitda;
          const ebitdaPct = lr.total.ebitda_pct;
          return (
            <Card key={lr.location} className="border-l-4" style={{ borderLeftColor: LOCATION_COLORS[i % LOCATION_COLORS.length] }}>
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <MapPin className="h-3.5 w-3.5" style={{ color: LOCATION_COLORS[i % LOCATION_COLORS.length] }} />
                  <span className="text-xs font-semibold uppercase tracking-wide">{lr.location}</span>
                </div>
                <div className="text-xl font-bold tabular-nums" data-testid={`location-revenue-${lr.location}`}>{formatEur(lr.total.revenue)}</div>
                <div className="text-[11px] text-muted-foreground">Ricavi · {lr.allocation_weight_var}%</div>
                <div className={`text-sm font-semibold mt-1.5 tabular-nums ${ebitda >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  EBITDA {formatEur(ebitda)}
                </div>
                <div className={`text-[11px] ${ebitda >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatPct(ebitdaPct)}</div>
              </CardContent>
            </Card>
          );
        })}
        {/* Azienda totale */}
        <Card className="border-l-4 border-slate-800 bg-slate-50">
          <CardContent className="pt-3 pb-2">
            <div className="flex items-center gap-1.5 mb-1">
              <Building2 className="h-3.5 w-3.5 text-slate-700" />
              <span className="text-xs font-semibold uppercase tracking-wide">Totale Sedi</span>
            </div>
            <div className="text-xl font-bold tabular-nums">{formatEur(analysis.company_total.revenue)}</div>
            <div className="text-[11px] text-muted-foreground">100%</div>
            <div className={`text-sm font-semibold mt-1.5 tabular-nums ${analysis.company_total.ebitda >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
              EBITDA {formatEur(analysis.company_total.ebitda)}
            </div>
            <div className={`text-[11px] ${analysis.company_total.ebitda >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatPct(analysis.company_total.ebitda_pct)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabella CE per Sede — pulita */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-indigo-600" /> Conto Economico per Sede ({analysis.months_loaded} mesi · {year})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="location-comparison-table">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-2 pl-2 font-semibold sticky left-0 bg-white z-10 min-w-[230px]">Voce</th>
                  {analysis.location_results.map((lr, i) => (
                    <th key={lr.location} className="text-right py-2 px-3 font-semibold min-w-[110px]" style={{ color: LOCATION_COLORS[i % LOCATION_COLORS.length] }}>
                      {lr.location}
                    </th>
                  ))}
                  <th className="text-right py-2 px-3 font-bold min-w-[110px] border-l-2 border-slate-300 bg-slate-50">Totale</th>
                </tr>
              </thead>
              <tbody>
                {CE_ROWS_LOC.map(row => {
                  const isCost = row.style === "cost";
                  const isHeader = row.style === "header";
                  const isTotal = row.style === "total";
                  const isSubtotal = row.style.startsWith("subtotal");
                  const bgClass = isTotal ? "bg-slate-800 text-white" :
                    row.style === "subtotal_green" ? "bg-emerald-50" :
                    row.style === "subtotal_blue" ? "bg-blue-50" :
                    row.style === "subtotal_indigo" ? "bg-indigo-50" :
                    isSubtotal ? "bg-slate-50" :
                    isHeader ? "bg-slate-100" : "";
                  const fontClass = (isSubtotal || isHeader || isTotal) ? "font-semibold" : "";
                  const indentPx = (row.indent || 0) * 14;
                  const companyVal = analysis.company_total[row.key] || 0;
                  const companyRev = analysis.company_total.revenue || 1;
                  const companyPct = row.key === "revenue" ? 100 : ((companyVal / companyRev) * 100);
                  const showPct = !isHeader;  // mostra % per tutte tranne header "RICAVI"

                  return (
                    <tr key={row.key} className={`border-b border-slate-100 ${bgClass}`}>
                      <td className={`py-1.5 sticky left-0 z-10 ${bgClass} ${fontClass}`} style={{ paddingLeft: `${10 + indentPx}px` }}>
                        {row.label}
                      </td>
                      {analysis.location_results.map((lr) => {
                        const val = lr.total[row.key] || 0;
                        const locRev = lr.total.revenue || 1;
                        const pct = row.key === "revenue" ? 100 : ((val / locRev) * 100);
                        return (
                          <td key={lr.location} className={`text-right tabular-nums px-3 py-1.5 ${fontClass} ${isCost && val > 0 ? 'text-red-600' : ''}`}>
                            <div>{isCost ? formatEur(-val) : formatEur(val)}</div>
                            {showPct && row.key !== "revenue" && (
                              <div className={`text-[10px] ${isTotal ? 'text-slate-300' : 'text-slate-500'}`}>{formatPct(pct)}</div>
                            )}
                          </td>
                        );
                      })}
                      <td className={`text-right tabular-nums px-3 py-1.5 border-l-2 border-slate-300 bg-slate-50 ${fontClass} ${isCost && companyVal > 0 ? 'text-red-600' : ''}`} style={isTotal ? {backgroundColor: '#1e293b'} : {}}>
                        <div>{isCost ? formatEur(-companyVal) : formatEur(companyVal)}</div>
                        {showPct && row.key !== "revenue" && (
                          <div className={`text-[10px] ${isTotal ? 'text-slate-300' : 'text-slate-500'}`}>{formatPct(companyPct)}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground mt-3 px-2">
              <strong>Logica:</strong> Ricavi netti del bilancio ripartiti per sede in base ai corrispettivi mensili. Costi variabili allocati in % al fatturato. Costi diretti (dipendenti, locazione, energia) presi dai bilanci per sede. Costi indiretti, ammortamenti, oneri finanziari e imposte divisi in <strong>parti uguali tra le {analysis.n_locations} sedi</strong>.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Costi Indiretti — breakdown sintetico */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-amber-600" /> Costi Indiretti (÷ {analysis.n_locations} sedi)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="p-3 bg-amber-50 rounded-lg">
              <div className="text-[11px] text-muted-foreground">Fissi Indiretti</div>
              <div className="font-bold tabular-nums text-amber-700">{formatEur(analysis.general_costs.fixed_costs)}</div>
              <div className="text-[10px] text-muted-foreground">{formatEur(analysis.general_costs.fixed_costs / analysis.n_locations)} / sede</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-[11px] text-muted-foreground">Variabili (pool)</div>
              <div className="font-bold tabular-nums">{formatEur(analysis.general_costs.variable_costs)}</div>
              <div className="text-[10px] text-muted-foreground">% fatturato</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-[11px] text-muted-foreground">Ammortamenti</div>
              <div className="font-bold tabular-nums">{formatEur(analysis.general_costs.depreciation)}</div>
              <div className="text-[10px] text-muted-foreground">{formatEur(analysis.general_costs.depreciation / analysis.n_locations)} / sede</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-[11px] text-muted-foreground">Oneri Finanziari</div>
              <div className="font-bold tabular-nums">{formatEur(analysis.general_costs.financial_charges)}</div>
              <div className="text-[10px] text-muted-foreground">{formatEur(analysis.general_costs.financial_charges / analysis.n_locations)} / sede</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-[11px] text-muted-foreground">Imposte</div>
              <div className="font-bold tabular-nums">{formatEur(analysis.general_costs.taxes)}</div>
              <div className="text-[10px] text-muted-foreground">{formatEur(analysis.general_costs.taxes / analysis.n_locations)} / sede</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Comparison */}
      {monthly?.monthly?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-emerald-600" /> Confronto Mensile per Sede - {year}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={monthly.monthly.map(m => {
                const row = { name: MONTH_NAMES[m.month - 1] || `M${m.month}` };
                monthly.locations.forEach(loc => { row[loc] = m[loc]?.revenue || 0; });
                return row;
              })} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatEur(v)} />
                <Legend />
                {monthly.locations.map((loc, i) => (
                  <Bar key={loc} dataKey={loc} fill={LOCATION_COLORS[i % LOCATION_COLORS.length]} radius={[4, 4, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
            {/* Monthly EBITDA table */}
            <div className="mt-4 overflow-x-auto max-h-[70vh] overflow-y-auto">
              <table className="w-full text-xs" data-testid="monthly-location-table">
                <thead className="bg-white">
                  <tr className="border-b">
                    <th className="text-left py-1 font-medium bg-white">Mese</th>
                    {monthly.locations.map((loc, i) => (
                      <React.Fragment key={loc}>
                        <th className="text-right py-1 px-2 font-medium" style={{ color: LOCATION_COLORS[i % LOCATION_COLORS.length] }}>Ricavi {loc}</th>
                        <th className="text-right py-1 px-2 font-medium" style={{ color: LOCATION_COLORS[i % LOCATION_COLORS.length] }}>EBITDA {loc}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthly.monthly.map(m => (
                    <tr key={m.month} className="border-b">
                      <td className="py-1 font-medium">{MONTH_NAMES[m.month - 1]}</td>
                      {monthly.locations.map(loc => (
                        <React.Fragment key={loc}>
                          <td className="text-right px-2 py-1 tabular-nums">{formatEur(m[loc]?.revenue)}</td>
                          <td className={`text-right px-2 py-1 tabular-nums ${(m[loc]?.ebitda || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {formatEur(m[loc]?.ebitda)}
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CE Annuale per Sede */}
      {yearlyCE?.location_data && (
        <LocationYearlyCESection yearlyCE={yearlyCE} expandedLocCE={expandedLocCE} setExpandedLocCE={setExpandedLocCE} />
      )}

      {/* Analisi per Sede: storico, proiezione e break-even */}
      {historyTarget?.results?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4 text-violet-600" /> Analisi per Sede — Storico Fatturato, Proiezione {historyTarget.year} e Break-Even
            </CardTitle>
            <p className="text-xs text-muted-foreground">Storico ricavi da Excel · Proiezione annua basata sui {historyTarget.months_loaded} mesi caricati · Target break-even: <strong>Settembre</strong></p>
          </CardHeader>
          <CardContent className="space-y-8">
            {historyTarget.results.map((sec, i) => {
              const locColor = LOCATION_COLORS[i % LOCATION_COLORS.length];
              const p = sec.projection;
              const be = sec.break_even;

              return (
                <div key={sec.location} className="space-y-4 pb-6 border-b last:border-b-0 last:pb-0">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" style={{ color: locColor }} />
                    <h3 className="font-bold text-lg" style={{ color: locColor }}>{sec.location}</h3>
                  </div>

                  {/* Storico fatturato annuo */}
                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-slate-700">📊 Storico Fatturato Annuo</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border" data-testid={`history-${sec.location}`}>
                        <thead className="bg-slate-50">
                          <tr>
                            {historyTarget.all_years.map(yr => (
                              <th key={yr} className="text-center py-2 px-3 font-semibold border-b">
                                {yr} {yr === historyTarget.year && <span className="text-[10px] text-violet-600">(parziale, {historyTarget.months_loaded}m)</span>}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {historyTarget.all_years.map(yr => {
                              const isCurrent = yr === historyTarget.year;
                              // Per anno corrente parziale: mostro YTD (solo mesi caricati nel bilancio)
                              // Per anni completi: mostro totale annuo
                              const v = isCurrent
                                ? ((sec.history_ytd && sec.history_ytd[yr]) || sec.history[yr] || 0)
                                : (sec.history[yr] || 0);
                              // Confronto:
                              //  - Anno corrente parziale: YTD corrente vs YTD anno precedente (stesso numero di mesi)
                              //  - Anni completi: totale annuo vs anno precedente
                              let variation = null;
                              let label = '';
                              if (isCurrent) {
                                const ytdPrev = (sec.history_ytd && sec.history_ytd[yr - 1]) || 0;
                                if (ytdPrev > 0) {
                                  variation = ((v - ytdPrev) / ytdPrev) * 100;
                                  label = `vs ${yr - 1} primi ${historyTarget.months_loaded}m (${formatEur(ytdPrev)})`;
                                }
                              } else {
                                const prevV = sec.history[yr - 1] || 0;
                                if (prevV > 0) {
                                  variation = ((v - prevV) / prevV) * 100;
                                  label = `vs ${yr - 1}`;
                                }
                              }
                              return (
                                <td key={yr} className="text-center py-3 px-3">
                                  <div className="text-lg font-bold tabular-nums">{formatEur(v)}</div>
                                  {isCurrent && (
                                    <div className="text-[10px] text-muted-foreground">primi {historyTarget.months_loaded} mesi</div>
                                  )}
                                  {variation !== null && (
                                    <div className={`text-xs mt-0.5 ${variation >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {variation >= 0 ? '▲' : '▼'} {Math.abs(variation).toFixed(1)}% {label}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Proiezione annua + Grafico Foto Reale */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Tabella proiezione */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold mb-3 text-slate-700">📈 Proiezione Annua {historyTarget.year}</h4>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-slate-200"><td className="py-1.5 font-medium">Ricavi annui</td><td className="text-right tabular-nums font-bold">{formatEur(p.revenue)}</td></tr>
                          <tr className="border-b border-slate-200"><td className="py-1.5 text-red-600 pl-3">(-) Costi Variabili ({p.variable_costs_pct}%)</td><td className="text-right tabular-nums text-red-600">{formatEur(-p.variable_costs)}</td></tr>
                          <tr className="border-b border-slate-200"><td className="py-1.5 text-red-600 pl-3">(-) Costi diretti sede</td><td className="text-right tabular-nums text-red-600">{formatEur(-p.direct_costs)}</td></tr>
                          <tr className="border-b border-slate-200"><td className="py-1.5 text-red-600 pl-3">(-) Costi indiretti ÷ {analysis.n_locations}</td><td className="text-right tabular-nums text-red-600">{formatEur(-p.indirect_fixed_costs)}</td></tr>
                          <tr className="border-b-2 border-slate-300"><td className="py-1.5 font-medium">Costi Totali</td><td className="text-right tabular-nums font-medium">{formatEur(p.variable_costs + p.direct_costs + p.indirect_fixed_costs)}</td></tr>
                          <tr className={`py-2 font-bold ${p.ebitda < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                            <td className="py-2 text-base">EBITDA proiettato</td>
                            <td className="text-right tabular-nums text-base">{formatEur(p.ebitda)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Grafico foto reale proiezione: Ricavi vs Costi Totali vs EBITDA (no aggiustamenti) */}
                    <div className="bg-white rounded-lg p-3 border">
                      <h4 className="text-sm font-semibold mb-1 text-slate-700">🎯 Proiezione {historyTarget.year} — Ricavi vs Costi</h4>
                      <p className="text-[11px] text-muted-foreground mb-2">Foto reale della proiezione annua basata sui {historyTarget.months_loaded} mesi caricati × {(12 / historyTarget.months_loaded).toFixed(2)}.</p>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart
                          data={[
                            { name: 'Ricavi', value: p.revenue, fill: '#10b981' },
                            { name: 'Costi Totali', value: p.variable_costs + p.direct_costs + p.indirect_fixed_costs, fill: '#ef4444' },
                            { name: 'EBITDA', value: p.ebitda, fill: p.ebitda >= 0 ? '#059669' : '#dc2626' },
                          ]}
                          margin={{ top: 20, right: 15, left: 0, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(v) => formatEur(v)} />
                          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                            {[0, 1, 2].map(i => null)}
                          </Bar>
                          {be.be_revenue_for_ebitda && (
                            <ReferenceLine y={be.be_revenue_for_ebitda} stroke="#7c3aed" strokeDasharray="4 4" label={{ value: `BEP Ricavi: ${formatEur(be.be_revenue_for_ebitda)}`, position: 'insideTopRight', fill: '#7c3aed', fontSize: 10 }} />
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Break-Even Target — Scenari (testo sotto, NO aggiustamenti nel grafico) */}
                  {p.ebitda < 0 ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-slate-800 mb-3">🎯 Come arrivare al Break-Even entro Settembre {historyTarget.year}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Scenario A: Riduzione Costi */}
                        <div className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-lg p-4">
                          <h5 className="font-semibold text-sm text-rose-900 mb-3">✂️ Scenario A — Riduzione costi</h5>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between border-b border-rose-100 pb-1">
                              <span>Su base <strong>annua</strong></span>
                              <span className="tabular-nums font-bold text-rose-700">{formatEur(be.cost_reduction_annual)} ({be.cost_reduction_pct}%)</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Su base <strong>mensile</strong></span>
                              <span className="tabular-nums font-bold text-rose-700">{formatEur(be.cost_reduction_monthly)}</span>
                            </div>
                            <p className="text-xs text-rose-700 italic mt-2">
                              Tagliare {formatEur(be.cost_reduction_monthly)}/mese di costi fissi per arrivare a EBITDA = 0 entro fine anno.
                            </p>
                          </div>
                        </div>

                        {/* Scenario B: Aumento Ricavi */}
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
                          <h5 className="font-semibold text-sm text-emerald-900 mb-3">📈 Scenario B — Aumento ricavi (BEP a Settembre)</h5>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between border-b border-emerald-100 pb-1">
                              <span>Ricavi prossimi {be.months_to_target} mesi <strong>(totale)</strong></span>
                              <span className="tabular-nums font-bold text-emerald-700">{formatEur(be.revenue_needed_remaining)}</span>
                            </div>
                            <div className="flex justify-between border-b border-emerald-100 pb-1">
                              <span>Ricavi <strong>mensili target</strong></span>
                              <span className="tabular-nums font-bold text-emerald-700">{formatEur(be.revenue_monthly_target)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>vs ricavi mensili attuali ({formatEur(be.revenue_monthly_current)})</span>
                              <span className={`tabular-nums font-bold ${be.revenue_increase_monthly_pct >= 0 ? 'text-emerald-700' : 'text-amber-600'}`}>
                                {be.revenue_increase_monthly_pct >= 0 ? '+' : ''}{be.revenue_increase_monthly_pct}%
                              </span>
                            </div>
                            <p className="text-xs text-emerald-700 italic mt-2">
                              Servono {formatEur(be.revenue_monthly_target)}/mese ({be.revenue_increase_monthly_pct >= 0 ? '+' : ''}{be.revenue_increase_monthly_pct}%) nei prossimi {be.months_to_target} mesi per chiudere l'anno in EBITDA = 0.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                      <p className="text-sm text-emerald-800">✅ <strong>{sec.location}</strong> è già sopra il break-even (EBITDA proiettato: <strong>{formatEur(p.ebitda)}</strong>).</p>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Setup Dialog */}
      <SetupDialog
        open={showSetup} onClose={() => setShowSetup(false)}
        setupLocations={setupLocations} setSetupLocations={setSetupLocations}
        setupMethod={setupMethod} setSetupMethod={setSetupMethod}
        assignments={assignments} setAssignments={setAssignments}
        detected={detected} saving={saving} onSave={saveSetup}
      />
    </div>
  );
};

// Setup Dialog Component
const SetupDialog = ({ open, onClose, setupLocations, setSetupLocations, setupMethod, setSetupMethod, assignments, setAssignments, detected, saving, onSave }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" /> Configurazione Multi-Sede
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Locations */}
          <div className="space-y-3">
            <Label className="font-semibold">Sedi / Unita Locali</Label>
            {detected?.locations?.length > 0 && setupLocations.length === 0 && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="text-blue-800 font-medium mb-2">Sedi rilevate automaticamente dai dati:</p>
                <div className="flex gap-2 flex-wrap">
                  {detected.locations.map(loc => (
                    <Badge key={loc} variant="outline" className="cursor-pointer border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => { if (!setupLocations.includes(loc)) setSetupLocations(prev => [...prev, loc]); }}
                    >{loc}</Badge>
                  ))}
                </div>
                <Button variant="link" size="sm" className="mt-1 p-0 h-auto text-blue-600" onClick={() => setSetupLocations([...detected.locations])} data-testid="use-detected-locations-btn">
                  Usa tutte le sedi rilevate
                </Button>
              </div>
            )}
            {setupLocations.map((loc, i) => (
              <div key={i} className="flex gap-2 items-center">
                <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <Input value={loc} onChange={e => { const nl = [...setupLocations]; nl[i] = e.target.value; setSetupLocations(nl); }}
                  placeholder="Nome sede" data-testid={`location-name-${i}`} />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 flex-shrink-0" onClick={() => setSetupLocations(setupLocations.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setSetupLocations([...setupLocations, ""])} data-testid="add-location-btn">
              <Plus className="h-4 w-4 mr-1" /> Aggiungi Sede
            </Button>
          </div>

          {/* Allocation Method */}
          <div className="space-y-2">
            <Label className="font-semibold">Metodo Ripartizione Costi Generali</Label>
            <Select value={setupMethod} onValueChange={setSetupMethod}>
              <SelectTrigger data-testid="allocation-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue_pct">In proporzione al fatturato (%)</SelectItem>
                <SelectItem value="equal">Divisi in parti uguali</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {setupMethod === "revenue_pct"
                ? "I costi generali vengono ripartiti in base alla percentuale di fatturato di ciascuna sede sul totale."
                : "I costi generali vengono divisi equamente tra tutte le sedi."}
            </p>
          </div>

          {/* Account Assignments */}
          {detected?.detected_accounts?.length > 0 && (
            <div className="space-y-2">
              <Label className="font-semibold">Assegnazione Conti Rilevati</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Per ogni conto sdoppiato, scegli se assegnarlo alla sede specifica o trattarlo come costo generale.
              </p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2 font-medium">Conto</th>
                      <th className="text-left p-2 font-medium">Sedi trovate</th>
                      <th className="text-center p-2 font-medium">Assegnazione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detected.detected_accounts.map(acc => (
                      <tr key={acc.account_code} className="border-t">
                        <td className="p-2">
                          <div className="font-mono text-xs text-muted-foreground">{acc.account_code}</div>
                          <div className="text-xs truncate max-w-[200px]">{acc.account_names[0]}</div>
                        </td>
                        <td className="p-2">
                          <div className="flex gap-1 flex-wrap">
                            {acc.detected_locations.map(l => <Badge key={l} variant="secondary" className="text-xs">{l}</Badge>)}
                          </div>
                        </td>
                        <td className="p-2 text-center">
                          <Select value={assignments[acc.account_code] || "location_specific"}
                            onValueChange={v => setAssignments(prev => ({ ...prev, [acc.account_code]: v }))}
                          >
                            <SelectTrigger className="w-[150px] mx-auto h-8 text-xs" data-testid={`assign-${acc.account_code}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="location_specific">Specifico sede</SelectItem>
                              <SelectItem value="general">Generale</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-location-config-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Salvataggio..." : "Salva e Analizza"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
